import { Redo2, Scissors, Undo2 } from "lucide-react";
import { Button } from "@amber/ui";
import type { PlayerState } from "./Player";
import { exact, secs, type Draft, type Edge } from "./timingDraft";

type MergeDirection = "previous" | "next";

export interface TurnInspectorProps {
  d: Draft;
  index: number;
  drafts: readonly Draft[];
  baseOf: Draft | undefined;
  errors: readonly string[];
  mergeChoice: MergeDirection | null;
  canUndo: boolean;
  canRedo: boolean;
  changed: boolean;
  player: PlayerState;
  speakerName: (id: string | null) => string;
  setCurrent: (index: number) => void;
  setEdge: (index: number, edge: Edge, ms: number, alone: boolean) => Draft[];
  commit: (next: Draft[]) => void;
  moveWord: (direction: "give" | "take") => void;
  merge: (direction: MergeDirection, speaker?: string | null) => void;
  setMergeChoice: (choice: MergeDirection | null) => void;
  split: () => void;
  undo: () => void;
  redo: () => void;
  onDiscard: () => void;
  onReview: () => void;
}

function InspectorHead({ d, index, drafts, baseOf, speakerName, setCurrent }: TurnInspectorProps) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-strong">{speakerName(d.speaker)}</span>
      <span className="text-muted">
        · turn {index + 1} of {drafts.length}
      </span>
      {baseOf && (baseOf.start !== d.start || baseOf.end !== d.end) ? (
        <span className="rounded-full border border-waiting px-1.5 text-sm">
          Changed · was {exact(baseOf.start)}–{exact(baseOf.end)}
        </span>
      ) : null}
      {d.words ? (
        <span className="rounded-full border border-waiting px-1.5 text-sm">Split part · {d.texts.length} words</span>
      ) : null}
      {d.sources.length > 1 ? <span className="rounded-full border border-waiting px-1.5 text-sm">Merged</span> : null}
      <span className="ml-auto flex gap-1">
        <Button
          size="sm"
          disabled={index <= 0}
          onClick={() => {
            setCurrent(index - 1);
          }}
        >
          Previous turn
        </Button>
        <Button
          size="sm"
          disabled={index >= drafts.length - 1}
          onClick={() => {
            setCurrent(index + 1);
          }}
        >
          Next turn
        </Button>
      </span>
    </div>
  );
}

function EdgeFields({ d, index, player, setEdge, commit }: TurnInspectorProps) {
  return (
    <div className="mt-2 flex flex-wrap items-end gap-3">
      {(["start", "end"] as const).map((edge) => (
        <label key={edge} className="flex flex-col gap-0.5">
          <span className="text-sm text-muted">{edge === "start" ? "Start (s)" : "End (s)"}</span>
          <span className="flex items-center gap-1">
            <input
              type="number"
              step={0.1}
              min={0}
              className="h-8 w-24 rounded-md border border-border bg-bg px-2 font-mono"
              value={(d[edge] / 1000).toFixed(1)}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (Number.isFinite(v)) commit(setEdge(index, edge, v * 1000, true));
              }}
            />
            <button
              type="button"
              className="text-link"
              onClick={() => {
                commit(setEdge(index, edge, player.timeMs, false));
              }}
            >
              Set to playhead
            </button>
          </span>
        </label>
      ))}
      <span className="pb-1.5 text-muted">Length {secs(d.end - d.start)}</span>
    </div>
  );
}

function WordMover({ d, index, drafts, moveWord }: TurnInspectorProps) {
  const nextD = drafts[index + 1];
  const sameSourceNext = d.words && nextD?.words && d.sources[0] === nextD.sources[0];
  if (!sameSourceNext) return null;
  return (
    <p className="m-0 mt-1.5 flex items-center gap-3 text-muted">
      Words: “{d.texts.slice(-3).join(" ")}” | “{nextD.texts.slice(0, 3).join(" ")}”
      <button
        type="button"
        className="text-link"
        onClick={() => {
          moveWord("give");
        }}
      >
        Move last word to the next part
      </button>
      <button
        type="button"
        className="text-link"
        onClick={() => {
          moveWord("take");
        }}
      >
        Take the next part's first word
      </button>
    </p>
  );
}

function MergeChoice({ d, index, drafts, mergeChoice, speakerName, merge, setMergeChoice }: TurnInspectorProps) {
  if (!mergeChoice) return null;
  const other = mergeChoice === "next" ? drafts[index + 1] : drafts[index - 1];
  return (
    <p className="m-0 mt-1.5 flex items-center gap-2">
      The speakers differ. Keep:
      {[...new Set([d.speaker, other?.speaker ?? null])].map((sp) => (
        <Button
          key={sp ?? "none"}
          size="sm"
          onClick={() => {
            merge(mergeChoice, sp);
          }}
        >
          {speakerName(sp)}
        </Button>
      ))}
      <button
        type="button"
        className="text-link"
        onClick={() => {
          setMergeChoice(null);
        }}
      >
        Cancel
      </button>
    </p>
  );
}

function InspectorActions(props: TurnInspectorProps) {
  const { d, index, drafts, errors, canUndo, canRedo, changed, player, merge, split, undo, redo } = props;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      <Button size="sm" onClick={split}>
        <Scissors size={13} aria-hidden="true" />
        Split at playhead
      </Button>
      <Button
        size="sm"
        disabled={!drafts[index - 1]}
        onClick={() => {
          merge("previous");
        }}
      >
        Merge with previous
      </Button>
      <Button
        size="sm"
        disabled={!drafts[index + 1]}
        onClick={() => {
          merge("next");
        }}
      >
        Merge with next
      </Button>
      <Button
        size="sm"
        onClick={() => {
          player.seek(Math.max(0, d.start - 2000), true);
        }}
      >
        Play boundary
      </Button>
      <Button size="sm" aria-label="Undo" disabled={!canUndo} onClick={undo}>
        <Undo2 size={13} aria-hidden="true" />
      </Button>
      <Button size="sm" aria-label="Redo" disabled={!canRedo} onClick={redo}>
        <Redo2 size={13} aria-hidden="true" />
      </Button>
      <Button size="sm" disabled={!changed} onClick={props.onDiscard}>
        Discard
      </Button>
      <Button
        size="sm"
        variant="primary"
        className="ml-auto"
        disabled={!changed || errors.length > 0}
        onClick={props.onReview}
      >
        Review and save…
      </Button>
    </div>
  );
}

export function TurnInspector(props: TurnInspectorProps) {
  return (
    <div className="mt-2 rounded-lg border border-divider bg-surface px-3 py-2 text-md" aria-label="Turn inspector">
      <InspectorHead {...props} />
      <EdgeFields {...props} />
      <WordMover {...props} />
      {props.errors.length > 0 ? (
        <ul className="m-0 mt-1.5 list-none p-0 text-attention" role="alert">
          {props.errors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      ) : null}
      <MergeChoice {...props} />
      <InspectorActions {...props} />
    </div>
  );
}
